// 1.9.10 main menu: desktop mega menu ([data-mega-nav], template-parts/nav.php) + mobile accordion ([data-mobile-nav]).
// Server markup works without JS (CSS :hover / :focus-within open the panels, mobile labels navigate). This module adds:
//  - hover intent (open after 120 ms, close 250 ms after leaving; switching between open items is instant)
//  - caret button = disclosure toggle (aria-expanded), Esc closes and returns focus, focus leaving the item closes it
//  - arrow keys: ←/→ (Home/End) between top links, ↓ opens + focuses the first panel link, ↑/↓ inside the panel
//  - touch: the first tap on a top link opens its panel, the second tap follows the link
//  - mobile: the chevron toggles the submenu (collapsed by default, one open at a time); the label always navigates
import { qs, qsa } from './dom.js';

const OPEN_DELAY = 120;
const CLOSE_DELAY = 250;
const SWITCH_DELAY = 90;

export function initNavMenu() {
  initDesktop(qs('[data-mega-nav]'));
  initMobile(qs('[data-mobile-nav]'));
}

function initDesktop(nav) {
  if (!nav) return;
  nav.dataset.ready = '1'; // switches the CSS :hover/:focus-within fallback off – JS state (.is-open) rules from here on
  const items = qsa('[data-nav-item]', nav);
  const tops = items.map((li) => qs('[data-nav-top]', li));
  let current = null;
  let timer = 0;
  let touchItem = null;

  const panelOf = (li) => qs('[data-nav-panel]', li);
  const caretOf = (li) => qs('[data-nav-caret]', li);
  const set = (li, open) => {
    if (!li || !panelOf(li)) return;
    li.classList.toggle('is-open', open);
    caretOf(li)?.setAttribute('aria-expanded', String(open));
  };
  const open = (li) => {
    clearTimeout(timer);
    if (current && current !== li) set(current, false);
    if (!panelOf(li)) { current = null; return; }
    set(li, true);
    current = li;
  };
  const close = (li = current) => {
    clearTimeout(timer);
    if (!li) return;
    set(li, false);
    if (current === li) current = null;
  };
  const links = (li) => qsa('[data-nav-link]', panelOf(li));

  items.forEach((li, i) => {
    li.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(timer);
      if (current === li) return;
      timer = setTimeout(() => open(li), current ? SWITCH_DELAY : OPEN_DELAY); // short switch delay: crossing a neighbour on the way into a panel does not swap it
    });
    li.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(timer);
      if (current === li) timer = setTimeout(() => close(li), CLOSE_DELAY);
    });
    li.addEventListener('focusout', (e) => {
      if (!li.contains(e.relatedTarget) && current === li) close(li);
    });
    const caret = caretOf(li);
    caret?.addEventListener('click', (e) => {
      e.preventDefault();
      if (current === li) close(li); else open(li);
    });
    const top = tops[i];
    top.addEventListener('pointerdown', (e) => { touchItem = e.pointerType !== 'mouse' && panelOf(li) && current !== li ? li : null; });
    top.addEventListener('click', (e) => {
      if (touchItem === li) { // first tap on touch: open instead of navigating
        e.preventDefault();
        touchItem = null;
        open(li);
      }
    });
    li.addEventListener('keydown', (e) => {
      const inPanel = panelOf(li)?.contains(e.target);
      if (e.key === 'Escape' && current === li) {
        e.preventDefault();
        close(li);
        (caret || top).focus();
        return;
      }
      if (!inPanel) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const n = tops.length;
          const j = (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
          if (current) close();
          tops[j].focus();
        } else if (e.key === 'Home' || e.key === 'End') {
          e.preventDefault();
          if (current) close();
          tops[e.key === 'Home' ? 0 : tops.length - 1].focus();
        } else if (e.key === 'ArrowDown' && panelOf(li)) {
          e.preventDefault();
          open(li);
          links(li)[0]?.focus();
        } else if (e.key === 'ArrowUp' && current === li) {
          e.preventDefault();
          close(li);
        }
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const list = links(li);
        const k = list.indexOf(document.activeElement);
        if (e.key === 'ArrowUp' && k <= 0) { (caret || top).focus(); return; }
        list[(k + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length]?.focus();
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        const list = links(li);
        list[e.key === 'Home' ? 0 : list.length - 1]?.focus();
      }
    });
  });

  document.addEventListener('click', (e) => { if (current && !current.contains(e.target)) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && current && !current.contains(e.target)) close(); });
  document.addEventListener('kt:overlay:open', () => close());
}

function initMobile(nav) {
  if (!nav) return;
  const toggles = qsa('[data-mnav-toggle]', nav);
  const setOpen = (btn, open) => {
    const sub = document.getElementById(btn.getAttribute('aria-controls'));
    if (!sub) return;
    sub.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    btn.closest('[data-mnav-item]')?.classList.toggle('is-open', open);
  };
  toggles.forEach((btn) => btn.addEventListener('click', (e) => {
    e.preventDefault();
    const open = btn.getAttribute('aria-expanded') !== 'true';
    toggles.forEach((b) => { if (b !== btn) setOpen(b, false); });
    setOpen(btn, open);
  }));
  // a fresh menu always starts collapsed
  document.addEventListener('kt:overlay:close', (e) => { if (e.detail?.name === 'menu') toggles.forEach((b) => setOpen(b, false)); });
}
